import React, { useRef } from 'react';
import { ErrorSpan } from './error-span.component';

interface ISixDigitInputProps {
  errors: any;
  name: string;
  register: any;
  children?: React.ReactNode;
  setFormattedValue: (val: string) => void;
  formattedValue: string;
}

export const SixDigitInput = ({
  errors,
  name,
  register,
  children,
  setFormattedValue,
  formattedValue
}: ISixDigitInputProps) => {
  const inputsRef = useRef<(HTMLInputElement | null)[]>([]);
  const raw = formattedValue.replace(/\s/g, '').padEnd(6, ' ');

  const setFormattedValueFromRaw = (
    rawStr: string,
    index: number,
    value: string = ' '
  ) => {
    const chars = rawStr.split('');
    chars[index] = value;
    const updatedRaw = chars.join('');
    const formatted = updatedRaw.replace(/(.{2})(.{2})(.{2})/, '$1 $2 $3');
    setFormattedValue(formatted);
  };

  const handleChange = (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 1);
    if (!value) return;
    setFormattedValueFromRaw(raw, index, value);
    if (index < 5) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === 'Backspace') {
      setFormattedValueFromRaw(raw, index);
      if (index > 0 && !raw[index]) {
        inputsRef.current[index - 1]?.focus();
      }
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');
    const digits = pastedText.replace(/\D/g, '').slice(0, 6);
    if (!digits) return;
    const newRaw = digits.padEnd(6, ' ');
    const formatted = newRaw.replace(/(.{2})(.{2})(.{2})/, '$1 $2 $3');
    setFormattedValue(formatted);
    const nextIndex = Math.min(digits.length, 5);
    inputsRef.current[nextIndex]?.focus();
  };

  return (
    <div className="flex flex-col relative">
      <div className="flex justify-center items-center gap-[12px]">
        {[0, 2, 4].map((groupStart, groupIndex) => (
          <React.Fragment key={groupStart}>
            <div className="flex gap-[8px]">
              {[0, 1].map((offset) => {
                const i = groupStart + offset;
                return (
                  <input
                    key={i}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={raw[i] === ' ' ? '' : raw[i]}
                    onChange={(e) => handleChange(i, e)}
                    onKeyDown={(e) => handleKeyDown(i, e)}
                    onPaste={handlePaste}
                    ref={(el) => {
                      inputsRef.current[i] = el;
                    }}
                    className={`
                      w-[44px] h-[44px] m-0
                      text-center text-[20px] font-body
                      rounded-[16px] border
                      ${errors[name] ? 'border-red-500' : 'border-[#BFBFC0] dark:border-[rgba(255,255,255,0.12)]'}
                      bg-white dark:bg-[#28282B] dark:text-white
                      focus:outline-none focus:border-[#818185]
                      focus:ring-[4px] focus:ring-[rgba(29,29,30,0.12)] focus:ring-opacity-20
                      dark:focus:border-[#5DE1FF]/40 dark:focus:ring-[rgba(93,225,255,0.12)]
                      transition-all
                    `}
                  />
                );
              })}
            </div>
            {groupIndex < 2 && (
              <span className="hidden md:inline text-[16px] font-regular text-gray-900 dark:text-[#A0A0A3]">
                -
              </span>
            )}
          </React.Fragment>
        ))}
      </div>

      <input type="hidden" value={formattedValue} {...register(name)} />

      {children}
      {errors[name] && <ErrorSpan>{errors[name].message}</ErrorSpan>}
    </div>
  );
};
